/**
 * @file SideBar.jsx
 * @author Rishikesh
 * @date 2024-09-09
 * @description Drawer sidebar component of this app.
 */
import {
  GridViewOutlined,
  LocalLibraryOutlined,
  ManageAccountsOutlined,
  PeopleAltOutlined,
  PowerSettingsNewOutlined,
  SentimentSatisfiedAltOutlined,
  SettingsOutlined,
  SummarizeOutlined,
  UploadFileOutlined,
} from "@mui/icons-material";
import {
  Box,
  colors,
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
  styled,
  useMediaQuery,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
} from "@mui/material";
import React from "react";
import { useDrawer } from "../context/DrawerContext";
import { useDispatch } from "react-redux";
import { signOut } from "../../store/features/authSlice";
import { useLocation, useNavigate } from "react-router-dom";

const allMenus = [
  {
    title: "Dashboard",
    icon: <GridViewOutlined />,
    state: "/dashboard",
  },
  {
    title: "Master Settings",
    icon: <ManageAccountsOutlined />,
    state: "/master-settings",
  },
  {
    title: "Users",
    icon: <PeopleAltOutlined />,
    state: "/users",
  },
  {
    title: "Leads",
    icon: <LocalLibraryOutlined />,
    state: "/leads",
  },
  {
    title: "Candidates",
    icon: <SentimentSatisfiedAltOutlined />,
    state: "/candidates",
  },
  {
    title: "Report",
    icon: <SummarizeOutlined />,
    state: "/report",
  },
  {
    title: "Import Excel",
    icon: <UploadFileOutlined />,
    state: "/import-excel",
  },
];
const footerMenu = [
  {
    title: "Settings",
    icon: <SettingsOutlined />,
    state: "/settings",
  },
  {
    title: "Logout",
    icon: <PowerSettingsNewOutlined />,
    state: "logout",
  },
];

export const HeaderStyle = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const SideBar = ({ sidebarWidth, role }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const activeState = location.pathname;

  const { isDrawerOpen, toggleDrawer } = useDrawer();
  const matchDownMD = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const [openLogoutDialog, setOpenLogoutDialog] = React.useState(false);

  const menus = React.useMemo(() => {
    if (role === "employee") {
      return allMenus.filter(
        (menu) =>
          menu.title === "Dashboard" ||
          menu.title === "Candidates" ||
          menu.title === "Leads" ||
          menu.title === "Import Excel"
      );
    }
    if (role === "agent") {
      return allMenus.filter(
        (menu) =>
          menu.title === "Dashboard" ||
          menu.title === "Candidates" ||
          menu.title === "Leads" ||
          menu.title === "Import Excel"
      );
    }
    return allMenus;
  }, [role]);

  const handleOpenLogoutDialog = () => {
    setOpenLogoutDialog(true);
  };

  const handleCloseLogoutDialog = () => {
    setOpenLogoutDialog(false);
  };

  const handleSignOut = () => {
    dispatch(signOut());
  };

  const openedMixin = (theme) => ({
    width: sidebarWidth,
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: "hidden",
  });

  const closedMixin = (theme) => ({
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: "hidden",
    width: 96,
    [theme.breakpoints.up("sm")]: {
      width: 96,
    },
  });

  const Drawer = styled(MuiDrawer, {
    shouldForwardProp: (prop) => prop !== "open",
  })(({ theme, open }) => ({
    width: sidebarWidth,
    flexShrink: 0,
    whiteSpace: "nowrap",
    boxSizing: "border-box",
    alignItems: "center",
    ...(open && {
      ...openedMixin(theme),
      "& .MuiDrawer-paper": openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      "& .MuiDrawer-paper": closedMixin(theme),
    }),
  }));

  const MenuItem = ({ item, isActive }) => {
    const handleClick = () => {
      if (item.state === "logout") {
        handleOpenLogoutDialog();
      } else {
        navigate(item.state);
      }
    };
    return (
      <ListItem
        disableGutters
        disablePadding
        sx={{
          borderRadius: "30px",
          height: "60px",
          p: 0.8,
          backgroundColor: isActive ? "#E4EBF4" : "",
        }}
      >
        <ListItemButton
          sx={{
            borderRadius: "25px",
            height: "50px",
            padding: 0,
            px: 0.4,
            alignItems: "center",
            justifyContent: "center",
            bgcolor: isActive ? "#3A80F7" : "",
            color: isActive ? colors.common.white : "",
            columnGap: 1,
            "&:hover": {
              bgcolor: isActive ? "#3A80F799" : "#E4EBF4",
              color: isActive ? colors.common.white : "",
            },
          }}
          onClick={handleClick}
        >
          <ListItemIcon
            sx={{
              width: "40px",
              height: "40px",
              borderRadius: "20px",
              minWidth: "40px",
              alignItems: "center",
              justifyContent: "center",
              color: isActive ? colors.common.white : "",
            }}
          >
            {item.icon}
          </ListItemIcon>
          {isDrawerOpen && (
            <ListItemText
              primary={<Typography fontWeight="600">{item.title}</Typography>}
              sx={{
                opacity: isDrawerOpen ? 1 : 0,
              }}
            />
          )}
        </ListItemButton>
      </ListItem>
    );
  };

  const drawerContent = (
    <Box
      paddingTop={2}
      paddingLeft={3}
      paddingBottom={2}
      display="flex"
      flexDirection="column"
      height="100vh"
      sx={{
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          px: 0.5,
          borderRadius: "36px",
        }}
        square
      >
        <List sx={{ py: 0.5 }}>
          {menus.map((item, index) => (
            <MenuItem
              key={`menu-item-${index}`}
              item={item}
              isActive={item.state === activeState}
            />
          ))}
        </List>
        <List sx={{ flex: 1 }} />
        <List sx={{ py: 0.5 }}>
          {footerMenu.map((item, index) => (
            <MenuItem
              key={`investment-item-${index}`}
              item={item}
              isActive={item.state === activeState}
            />
          ))}
        </List>
      </Paper>
    </Box>
  );

  return (
    <>
      {!matchDownMD ? (
        <Drawer
          variant="permanent"
          open={isDrawerOpen}
          onClose={toggleDrawer}
          sx={{
            display: { xs: "none", sm: "none", md: "block" },
            "& .MuiDrawer-paper": {
              borderWidth: 0,
              backgroundColor: "#E4EBF4",
              "&::-webkit-scrollbar": { display: "none" },
            },
          }}
        >
          <HeaderStyle />
          {drawerContent}
        </Drawer>
      ) : (
        <MuiDrawer
          variant="temporary"
          open={isDrawerOpen}
          onClose={toggleDrawer}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: "block", sm: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              borderWidth: 0,
              width: sidebarWidth,
              pr: 3,
              "&::-webkit-scrollbar": { display: "none" },
            },
          }}
        >
          <HeaderStyle />
          {drawerContent}
        </MuiDrawer>
      )}
      <Dialog
        open={openLogoutDialog}
        onClose={handleCloseLogoutDialog}
        aria-labelledby="logout-confirm-dialog-title"
        aria-describedby="logout-confirm-dialog-description"
      >
        <DialogTitle id="logout-confirm-dialog-title">
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="logout-confirm-dialog-description">
            Are you sure you want to log out?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogoutDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleSignOut();
              handleCloseLogoutDialog();
            }}
            color="primary"
            autoFocus
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SideBar;
